import React from 'react';
import { confirmProviderEmailChange, requestProviderEmailChange, type ProviderEmailChangeResponse } from '@kikodacode/vbi-cares-api';
import { useMutation, useQuery } from 'react-query';
import { parseEmailChangeError } from 'utils/emailErrorParser';
import { formatProviderName } from 'utils/formatProviderName';
import { axiosInstance } from './axiosConfig';
import { queryKeys } from './constants';
import type { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import type { ValidationError } from './usePhysicianEmailStatement';

export interface EmailChangeRequest {
  providerId: number;
  newEmail: string;
  recaptchaToken: string;
}

export type EmailChangeApiError = AxiosError<ValidationError[] | { message?: string; nextAvailableTime?: string } | string>;

export interface EmailChangeTarget {
  id: string;
  firstName: string;
  lastName: string;
  maskedEmail?: string | null;
}

interface EmailChangeRecipient {
  id: string;
  name: string;
  maskedEmail?: string | null;
}

interface RequestHookProps {
  axios?: AxiosInstance;
}

export const useRequestProviderEmailChange = ({ axios = axiosInstance }: RequestHookProps = {}) => {
  const mutation = useMutation<AxiosResponse<ProviderEmailChangeResponse>, EmailChangeApiError, EmailChangeRequest>(
    payload =>
      requestProviderEmailChange(
        payload.providerId,
        {
          newEmail: payload.newEmail,
          recaptchaToken: payload.recaptchaToken,
        },
        axios,
      ),
    { retry: false },
  );

  return {
    requestEmailChange: mutation.mutate,
    requestEmailChangeAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    data: mutation.data,
    error: mutation.error,
    reset: mutation.reset,
  };
};

export const useEmailChangeDialog = () => {
  const [recipient, setRecipient] = React.useState<EmailChangeRecipient | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const { requestEmailChange, isLoading, isSuccess, isError, data, error, reset } = useRequestProviderEmailChange();
  const parsedError = isError ? parseEmailChangeError(error) : undefined;

  const openDialog = React.useCallback(
    (item: EmailChangeTarget) => {
      reset();
      setIsOpen(true);
      setRecipient({
        id: item.id,
        name: formatProviderName(item.firstName, item.lastName),
        maskedEmail: item.maskedEmail,
      });
    },
    [reset],
  );

  const closeDialog = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const clearAfterExit = React.useCallback(() => {
    reset();
    setRecipient(null);
  }, [reset]);

  const submit = React.useCallback(
    (newEmail: string, recaptchaToken: string) => {
      if (!recipient || isLoading) return;

      requestEmailChange({
        providerId: Number(recipient.id),
        newEmail,
        recaptchaToken,
      });
    },
    [isLoading, recipient, requestEmailChange],
  );

  return {
    isOpen,
    recipient,
    successMessage: isSuccess ? (data?.data.message ?? null) : null,
    fieldError: parsedError?.field === 'newEmail' ? parsedError.message : undefined,
    formError: parsedError && parsedError.field !== 'newEmail' ? parsedError.message : undefined,
    formErrorSeverity: parsedError && parsedError.field !== 'newEmail' ? parsedError.severity : undefined,
    isLoading,
    openDialog,
    closeDialog,
    clearAfterExit,
    submit,
  };
};

interface EmailConfirmationResult {
  status: 'loading' | 'success' | 'error';
  message: string;
  maskedEmail?: string;
  retry?: () => void;
}

export const INVALID_CONFIRMATION_MESSAGE = 'This confirmation link is invalid or has expired.';

interface UseEmailConfirmationOptions extends RequestHookProps {
  onSuccess?: () => void;
}

export const useEmailConfirmation = (
  token: string,
  { onSuccess, axios = axiosInstance }: UseEmailConfirmationOptions = {},
): EmailConfirmationResult => {
  // A new callback identity must not refetch. The latest function is read when the query succeeds.
  const onSuccessRef = React.useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const handleSuccess = React.useCallback(() => {
    onSuccessRef.current?.();
  }, []);

  const query = useQuery(queryKeys.emailConfirmation(token), () => confirmProviderEmailChange(token, axios), {
    enabled: Boolean(token),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    onSuccess: handleSuccess,
  });

  const retry = React.useCallback(() => {
    void query.refetch();
  }, [query]);

  if (!token) {
    return {
      status: 'error',
      message: INVALID_CONFIRMATION_MESSAGE,
    };
  }

  if (query.isLoading || (query.isFetching && !query.isSuccess)) {
    return { status: 'loading', message: '' };
  }

  if (query.isError) {
    return {
      status: 'error',
      message: parseEmailChangeError(query.error).message,
      retry,
    };
  }

  if (query.isSuccess) {
    return {
      status: 'success',
      message: query.data.data.message,
      maskedEmail: query.data.data.maskedEmail,
    };
  }

  return { status: 'loading', message: '' };
};
